import { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { darkTheme } from '../../lib/tokens';
import { fetchAudit, type AuditFilters } from './api';
import type { AuditEntry } from './types';

const PAGE_SIZE = 100;

export function AuditLogPage(): JSX.Element {
  const [actorEmail, setActorEmail] = useState('');
  const [targetType, setTargetType] = useState('');
  const [actionPrefix, setActionPrefix] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  const filters = useMemo<AuditFilters>(
    () => ({
      actorEmail: actorEmail.trim() || undefined,
      targetType: targetType || undefined,
      action: actionPrefix.trim() || undefined,
      from: from || undefined,
      to: to || undefined,
      limit: PAGE_SIZE,
    }),
    [actorEmail, targetType, actionPrefix, from, to],
  );

  const query = useQuery({
    queryKey: ['audit', filters],
    queryFn: () => fetchAudit(filters),
  });

  function toggleExpanded(id: string) {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  return (
    <div className="mx-auto max-w-[1400px]">
      <header className="mb-4 flex items-end justify-between">
        <div>
          <h1 className="text-2xl font-bold text-text-primary">Audit-Log</h1>
          <p className="mt-1 text-xs text-text-tertiary">
            Append-only Trail aller Admin-Aktionen. {query.data?.items.length ?? 0} Treffer
            {query.data?.hasMore ? ' (mehr verfügbar — Filter verfeinern)' : ''}
          </p>
        </div>
      </header>

      <FilterBar
        actorEmail={actorEmail}
        onActorEmailChange={setActorEmail}
        targetType={targetType}
        onTargetTypeChange={setTargetType}
        actionPrefix={actionPrefix}
        onActionPrefixChange={setActionPrefix}
        from={from}
        onFromChange={setFrom}
        to={to}
        onToChange={setTo}
        facets={query.data?.facets}
      />

      {query.error && (
        <div
          className="mt-4 rounded-md p-3 text-sm"
          style={{ background: darkTheme.colors.dangerBg, color: darkTheme.colors.danger }}
        >
          {query.error instanceof Error ? query.error.message : 'Fehler'}
        </div>
      )}

      {query.isLoading && (
        <div className="mt-4 space-y-2">
          {Array.from({ length: 8 }).map((_, i) => (
            <div
              key={i}
              className="h-14 animate-pulse rounded-md"
              style={{ background: darkTheme.colors.bgSurface }}
            />
          ))}
        </div>
      )}

      {query.data && query.data.items.length === 0 && (
        <div
          className="mt-4 rounded-md border p-12 text-center text-sm text-text-secondary"
          style={{
            background: darkTheme.colors.bgSurface,
            borderColor: darkTheme.colors.borderSubtle,
          }}
        >
          Keine Audit-Einträge für die aktuellen Filter.
        </div>
      )}

      <div className="mt-4 space-y-2">
        {query.data?.items.map((entry) => (
          <Row
            key={entry.id}
            entry={entry}
            expanded={expanded.has(entry.id)}
            onToggle={() => toggleExpanded(entry.id)}
          />
        ))}
      </div>
    </div>
  );
}

interface FilterBarProps {
  actorEmail: string;
  onActorEmailChange: (v: string) => void;
  targetType: string;
  onTargetTypeChange: (v: string) => void;
  actionPrefix: string;
  onActionPrefixChange: (v: string) => void;
  from: string;
  onFromChange: (v: string) => void;
  to: string;
  onToChange: (v: string) => void;
  facets?: import('./types').AuditFacets;
}

function FilterBar(props: FilterBarProps): JSX.Element {
  return (
    <div
      className="flex flex-wrap items-center gap-2 rounded-md border p-3"
      style={{
        background: darkTheme.colors.bgSurface,
        borderColor: darkTheme.colors.borderSubtle,
      }}
    >
      <input
        type="search"
        placeholder="Admin-Email…"
        value={props.actorEmail}
        onChange={(e) => props.onActorEmailChange(e.target.value)}
        className="min-w-[180px] rounded-md border bg-bg-base px-3 py-1.5 text-sm text-text-primary outline-none focus:border-accent"
        style={{ borderColor: darkTheme.colors.borderSubtle }}
      />

      <select
        value={props.targetType}
        onChange={(e) => props.onTargetTypeChange(e.target.value)}
        className="rounded-md border bg-bg-base px-2 py-1.5 text-xs text-text-primary outline-none focus:border-accent"
        style={{ borderColor: darkTheme.colors.borderSubtle }}
      >
        <option value="">Alle Target-Typen</option>
        {props.facets?.targetTypes.map((t) => (
          <option key={t.type} value={t.type}>
            {t.type} ({t.count})
          </option>
        ))}
      </select>

      <input
        type="text"
        placeholder="Action-Prefix (z.B. user.)"
        value={props.actionPrefix}
        onChange={(e) => props.onActionPrefixChange(e.target.value)}
        className="min-w-[180px] rounded-md border bg-bg-base px-3 py-1.5 text-sm text-text-primary outline-none focus:border-accent"
        style={{ borderColor: darkTheme.colors.borderSubtle }}
      />

      <input
        type="date"
        value={props.from}
        onChange={(e) => props.onFromChange(e.target.value)}
        className="rounded-md border bg-bg-base px-2 py-1.5 text-xs text-text-primary outline-none focus:border-accent"
        style={{ borderColor: darkTheme.colors.borderSubtle }}
      />
      <span className="text-text-tertiary">–</span>
      <input
        type="date"
        value={props.to}
        onChange={(e) => props.onToChange(e.target.value)}
        className="rounded-md border bg-bg-base px-2 py-1.5 text-xs text-text-primary outline-none focus:border-accent"
        style={{ borderColor: darkTheme.colors.borderSubtle }}
      />

      {props.facets && props.facets.topActions.length > 0 && (
        <div className="ml-auto text-[11px] text-text-tertiary">
          Top:{' '}
          {props.facets.topActions.slice(0, 4).map((a, i) => (
            <span key={a.action}>
              {i > 0 && ' · '}
              <button
                type="button"
                onClick={() => props.onActionPrefixChange(a.action)}
                className="hover:text-accent-light"
              >
                {a.action} ({a.count})
              </button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

function Row({
  entry,
  expanded,
  onToggle,
}: {
  entry: AuditEntry;
  expanded: boolean;
  onToggle: () => void;
}): JSX.Element {
  const hasDiff = entry.before != null || entry.after != null;
  const targetLink = makeTargetLink(entry.targetType, entry.targetId);

  return (
    <div
      className="rounded-md border"
      style={{
        background: darkTheme.colors.bgSurface,
        borderColor: darkTheme.colors.borderSubtle,
      }}
    >
      <button
        type="button"
        onClick={onToggle}
        className="block w-full px-4 py-3 text-left transition-colors hover:bg-bg-elevated"
      >
        <div className="flex items-baseline justify-between gap-3">
          <div className="flex items-center gap-2">
            <code
              className="rounded px-1.5 py-0.5 text-[11px] font-semibold text-accent-light"
              style={{ background: darkTheme.colors.accentSubtle }}
            >
              {entry.action}
            </code>
            {entry.stepUpUsed && (
              <span
                className="rounded-full px-1.5 py-0.5 text-[9px] font-semibold uppercase"
                style={{
                  background: darkTheme.colors.warningBg,
                  color: darkTheme.colors.warning,
                }}
              >
                Step-up
              </span>
            )}
            <span className="text-[11px] text-text-tertiary">
              {entry.targetType}
              {entry.targetId && ` · ${entry.targetId.slice(0, 12)}`}
            </span>
          </div>
          <span className="text-[11px] text-text-tertiary whitespace-nowrap">
            {formatDate(entry.createdAt)}
          </span>
        </div>
        <div className="mt-1 flex items-baseline gap-2 text-xs text-text-secondary">
          <span>
            Actor:{' '}
            <span className="text-text-primary">
              {entry.user?.email ?? entry.user?.id ?? '—'}
            </span>
          </span>
          {entry.ipAddress && (
            <span className="text-text-tertiary">{entry.ipAddress}</span>
          )}
          {entry.metadata?.reason ? (
            <span className="truncate">→ {String(entry.metadata.reason)}</span>
          ) : null}
        </div>
      </button>

      {expanded && (
        <div
          className="border-t p-4"
          style={{ borderColor: darkTheme.colors.borderSubtle }}
        >
          <dl className="mb-3 grid grid-cols-1 gap-y-1 text-xs sm:grid-cols-[140px_1fr]">
            <dt className="text-text-tertiary">Target</dt>
            <dd className="text-text-primary">
              {targetLink ? (
                <Link to={targetLink} className="text-accent-light hover:underline">
                  {entry.targetType} {entry.targetId}
                </Link>
              ) : (
                <span className="font-mono">
                  {entry.targetType} {entry.targetId}
                </span>
              )}
            </dd>
            <dt className="text-text-tertiary">User-Agent</dt>
            <dd className="break-all text-text-secondary">
              {entry.userAgent ?? '—'}
            </dd>
            {entry.metadata && Object.keys(entry.metadata).length > 0 && (
              <>
                <dt className="text-text-tertiary">Metadata</dt>
                <dd>
                  <pre className="overflow-auto rounded bg-bg-base p-2 font-mono text-[11px] text-text-primary">
                    {JSON.stringify(entry.metadata, null, 2)}
                  </pre>
                </dd>
              </>
            )}
          </dl>

          {hasDiff && (
            <div className="grid gap-3 sm:grid-cols-2">
              <DiffPane label="Before" value={entry.before} />
              <DiffPane label="After" value={entry.after} />
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function DiffPane({ label, value }: { label: string; value: unknown }): JSX.Element {
  return (
    <div>
      <div className="mb-1 text-[10px] font-semibold uppercase tracking-wide text-text-tertiary">
        {label}
      </div>
      <pre
        className="overflow-auto rounded p-2 font-mono text-[11px]"
        style={{
          background: darkTheme.colors.bgBase,
          color: darkTheme.colors.textPrimary,
          maxHeight: 200,
        }}
      >
        {value === null || value === undefined
          ? <span className="text-text-tertiary">null</span>
          : JSON.stringify(value, null, 2)}
      </pre>
    </div>
  );
}

function makeTargetLink(type: string, id: string): string | null {
  if (type === 'user') return `/users/${id}`;
  // feedback/announcement/broadcast haben aktuell keine Detail-Routes
  // mit ID-Param — Liste wird stattdessen geöffnet.
  return null;
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleString('de-DE', {
    day: '2-digit',
    month: '2-digit',
    year: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
}
